import type {AudienceDataField, FieldMappedData} from '@Panels/add-mailing-list-panel/add-mailing-list-panel.types';
import {AudienceDefaultField, CUSTOM_FIELD_KEY_PREFIX} from '@Panels/add-mailing-list-panel/add-mailing-list-panel.types';
import type {
  AddContactToMailingListResponse,
  CreateMailingListResponse,
  ParseAudienceDataAsyncResponse,
  ParseAudienceDataResponse,
} from '@Components/mailing-list-wizard/mailing-list-wizard.types';
import {applyLetterCase, getCamelCasedString, LetterCase} from '@Utils/string.util';
import type {
  AudienceCustomFieldData,
  RawAudience,
  RawAudienceFieldNames,
  UserCustomFieldMeta,
  UserCustomFieldMetaMap,
} from '@Components/email-marketing-campaign-audience/email-marketing-campaign-audience.types';

const MAILING_LIST_PARSE_DATA_TIMEOUT_MS = 600000;

export const getAudienceListItemDisplayText = (audienceField: AudienceDataField, userCustomFieldsMetaMap: UserCustomFieldMetaMap | null): string => {
  if (!isAudienceDataFieldADefaultField(audienceField) && userCustomFieldsMetaMap) {
    if (!Object.keys(userCustomFieldsMetaMap).includes(String(audienceField))) {
      throw new Error('No custom field found for custom field ID');
    }
    return userCustomFieldsMetaMap[audienceField].label;
  }

  switch (audienceField) {
    case AudienceDefaultField.EMAIL:
      return window.i18next.t('pmwjs_email');
    case AudienceDefaultField.FIRST_NAME:
      return applyLetterCase(window.i18next.t('pmwjs_first_name'), LetterCase.SENTENCE_CASE);
    case AudienceDefaultField.LAST_NAME:
      return applyLetterCase(window.i18next.t('pmwjs_last_name'), LetterCase.SENTENCE_CASE);
    case AudienceDefaultField.ADDRESS_COUNTRY:
      return window.i18next.t('pmwjs_address_country');
    case AudienceDefaultField.ADDRESS_STATE:
      return window.i18next.t('pmwjs_address_state');
    case AudienceDefaultField.ADDRESS_CITY:
      return window.i18next.t('pmwjs_address_city');
    case AudienceDefaultField.ADDRESS_STREET:
      return window.i18next.t('pmwjs_address_street');
    case AudienceDefaultField.BIRTHDAY:
      return window.i18next.t('pmwjs_birthday');
    case AudienceDefaultField.PHONE_NUMBER:
      return window.i18next.t('pmwjs_phone_number');
    case AudienceDefaultField.SKIP_THIS_FIELD:
      return window.i18next.t('pmwjs_skip_this_field');
    default:
      return '-';
  }
};

export const isAudienceDataFieldADefaultField = (audienceDataField: AudienceDataField | string): audienceDataField is AudienceDefaultField => {
  return Object.values(AudienceDefaultField).includes(audienceDataField as AudienceDefaultField);
};

export const isAudienceFieldAnAddressField = (audienceDataField: AudienceDataField | string): audienceDataField is AudienceDefaultField => {
  if (!isAudienceDataFieldADefaultField(audienceDataField)) {
    return false;
  }

  switch (audienceDataField) {
    case AudienceDefaultField.ADDRESS_CITY:
    case AudienceDefaultField.ADDRESS_COUNTRY:
    case AudienceDefaultField.ADDRESS_STATE:
    case AudienceDefaultField.ADDRESS_STREET:
      return true;
    default:
      return false;
  }
};

export const getAudienceFieldText = (audienceField: AudienceDataField): string => {
  if (!isAudienceDataFieldADefaultField(audienceField)) {
    return String(audienceField);
  }

  return getCamelCasedString(AudienceDefaultField[audienceField].trim(), '_');
};

export const getAudienceDataFieldFromAudienceFieldName = (fieldName: string): AudienceDataField => {
  let matchedDefaultField: AudienceDataField = AudienceDefaultField.NOT_SELECTED;
  Object.values(AudienceDefaultField).forEach((defaultFieldName) => {
    if (getCamelCasedString(defaultFieldName).toLowerCase() === fieldName.trim().toLowerCase()) {
      matchedDefaultField = defaultFieldName as AudienceDefaultField;
    }
  });

  return matchedDefaultField;
};

export const getRawAudienceKeyFromAudienceDataField = (audienceDataField: AudienceDataField): RawAudienceFieldNames | null => {
  switch (audienceDataField) {
    case AudienceDefaultField.EMAIL:
      return 'email';
    case AudienceDefaultField.FIRST_NAME:
      return 'firstName';
    case AudienceDefaultField.LAST_NAME:
      return 'lastName';
    case AudienceDefaultField.ADDRESS:
      return 'address';
    case AudienceDefaultField.BIRTHDAY:
      return 'birthday';
    case AudienceDefaultField.PHONE_NUMBER:
      return 'phoneNumber';
    default:
      return null;
  }
};

// This gets the type of an attribute in a type/interface
export type GetTypeOfProp<T, K extends keyof T> = T[K];

const ALLOWED_NAME_CHARACTERS = 'a-zA-Z0-9 ,\\[\\]_)(.!\\-+';
const ALLOWED_NAME_PATTERN = new RegExp(`^([${ALLOWED_NAME_CHARACTERS}]+)$`);
const FORBIDDEN_CHARACTERS = new RegExp(`[^${ALLOWED_NAME_CHARACTERS}]`, 'g');
const ACCENT_MARKS = /\p{Diacritic}/gu;
const EXTRA_SPACES = /\s+/g;

export const isMailingListNameValid = (mailingListName: string): boolean => {
  return ALLOWED_NAME_PATTERN.test(mailingListName);
};

export const sanitizeMailingListName = (name: string): string => {
  return name.normalize('NFD').replace(ACCENT_MARKS, '').replace(FORBIDDEN_CHARACTERS, '').replace(EXTRA_SPACES, ' ').trim();
};

export const requestParseMailingListUserInput = async (userInput: string | File): Promise<ParseAudienceDataResponse> => {
  if (!userInput) {
    return {audienceData: [], numAudiencesOverLimit: 0, numSubmittedAudiences: 0, numValidAudiences: 0, errorCode: 'unknown'};
  }

  try {
    return (await window.PMW.pollingAjaxCallAsync(
      sendAudienceDataForParsing.bind(null, userInput),
      checkAudienceParseStatus,
      MAILING_LIST_PARSE_DATA_TIMEOUT_MS
    )) as ParseAudienceDataResponse;
  } catch (e) {
    console.error(e);
    return {audienceData: [], numAudiencesOverLimit: 0, numSubmittedAudiences: 0, numValidAudiences: 0, errorCode: 'unknown'};
  }
};

export const getErrorMessageOnAddingEmails = (errorCode: ParseAudienceDataResponse['errorCode']): string => {
  if (errorCode === 'invalid-mailing-list-name') {
    return window.i18next.t('pmwjs_campaign_error_invalid_mailing_list_name');
  }

  if (errorCode === 'duplicate-emails') {
    return window.i18next.t('pmwjs_mailinglist_error_duplicate_emails');
  }

  if (errorCode === 'all-invalid-emails') {
    return window.i18next.t('pmwjs_your_data_has_invalid_format');
  }

  if (errorCode === 'emails-limit-reached') {
    return window.i18next.t('pmwjs_reached_limit_emails_error');
  }

  return window.i18next.t('pmwjs_your_data_has_invalid_format');
};

const sendAudienceDataForParsing = async (userInput: string | File): Promise<ParseAudienceDataAsyncResponse> => {
  let data;
  if (userInput instanceof File) {
    const fd = new FormData();
    fd.append('file', userInput);
    data = fd;
  } else {
    data = {emailList: userInput};
  }

  return (await window.PMW.writeLocal('emailmarketing/parseAudienceDataAsync', data)) as ParseAudienceDataAsyncResponse;
};

const checkAudienceParseStatus = async (mailinglistParseData: ParseAudienceDataAsyncResponse): Promise<ParseAudienceDataResponse | null> => {
  return (await window.PMW.readLocal('emailmarketing/checkParseAudienceDataStatus', {
    mailingListParseStartTime: mailinglistParseData.mailingListParseStartTime,
  })) as ParseAudienceDataResponse;
};

export const requestAddCustomFieldForUser = async (customFieldLabel: string): Promise<UserCustomFieldMeta | null> => {
  if (!customFieldLabel) {
    return null;
  }

  try {
    return (await window.PMW.writeLocal('emailmarketing/addCustomFieldForUser', {label: customFieldLabel})) as UserCustomFieldMeta;
  } catch (e) {
    console.error(e);
    return null;
  }
};

export const requestCreateMailingList = async (
  audiences: RawAudience[],
  mailingListName: string,
  idEmailMarketingCampaign: string | null = null,
  doLoadAudiences = false
): Promise<CreateMailingListResponse | null> => {
  if (!audiences) {
    return null;
  }

  const data = {
    mailingListName,
    validAudiences: JSON.stringify(audiences),
    doLoadAudiences,
    ...(idEmailMarketingCampaign && {idEmailMarketingCampaign}),
  };

  try {
    const response: CreateMailingListResponse = (await window.PMW.writeLocal('emailmarketing/createMailingListV2', data)) as CreateMailingListResponse;
    if (!response) {
      return null;
    }

    return response;
  } catch (e) {
    console.error(e);
    return null;
  }
};

export const requestAddContactsToMailingList = async (
  audiences: RawAudience[],
  idMailingList: number,
  doLoadAudiences = false
): Promise<AddContactToMailingListResponse | null> => {
  const data = {
    idMailingList,
    validAudiences: JSON.stringify(audiences),
    doLoadAudiences,
  };

  try {
    const response = (await window.PMW.writeLocal('emailmarketing/addToMailingListV2', data)) as CreateMailingListResponse;
    if (!response) {
      return null;
    }
    return response;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const getUserCustomFields = async (): Promise<UserCustomFieldMetaMap> => {
  try {
    const response = (await window.PMW.readLocal('emailmarketing/getUserCustomFieldsMeta')) as UserCustomFieldMeta[];
    let userCustomFieldMetaMap: UserCustomFieldMetaMap = {};
    response.forEach((userCustomFieldMeta: UserCustomFieldMeta) => {
      userCustomFieldMetaMap = {
        ...userCustomFieldMetaMap,
        [userCustomFieldMeta.idUserHasCustomField]: userCustomFieldMeta,
      };
    });

    return userCustomFieldMetaMap;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const getMaxCustomFieldsFromAudienceList = (audienceList: RawAudience[]): number => {
  let maxCustomFields = 0;
  audienceList.forEach((audienceItem: RawAudience) => {
    if (audienceItem.customFields) {
      if (Object.keys(audienceItem.customFields).length > maxCustomFields) {
        maxCustomFields = Object.keys(audienceItem.customFields).length;
      }
    }
  });

  return maxCustomFields;
};
export const getFieldMappedDataFromAudienceData = (audienceData: RawAudience[]): FieldMappedData => {
  const maxCustomFieldsInData = getMaxCustomFieldsFromAudienceList(audienceData);
  const fieldNameMap: Record<string, (string | null)[]> = {};
  const shouldFieldBeKept: Record<string, boolean> = {};

  audienceData.forEach((rawAudienceItem: RawAudience) => {
    Object.entries(rawAudienceItem).forEach(([fieldName, fieldValue]) => {
      if (fieldName !== 'customFields') {
        if (fieldValue) {
          shouldFieldBeKept[fieldName] = true;
        }

        let valueToAdd: string | null;
        if (!fieldValue) {
          valueToAdd = null;
        } else if (fieldName === 'birthday' && 'date' in fieldValue) {
          // eslint-disable-next-line
          valueToAdd = new Date(fieldValue.date).toDateString();
        } else {
          valueToAdd = String(fieldValue);
        }
        if (fieldName in fieldNameMap) {
          fieldNameMap[fieldName].push(valueToAdd);
        } else {
          fieldNameMap[fieldName] = [valueToAdd];
        }
        return;
      }

      let customFieldData: AudienceCustomFieldData = {};
      if (fieldValue) {
        customFieldData = fieldValue as AudienceCustomFieldData;
      }

      const customFieldDataValues = Object.values(customFieldData);
      for (let i = 0; i < customFieldDataValues.length; i++) {
        const processedResponseKey = `${CUSTOM_FIELD_KEY_PREFIX}${i + 1}`;
        const valueToAdd = customFieldDataValues[i] ? String(customFieldDataValues[i]) : null;
        if (valueToAdd) {
          shouldFieldBeKept[processedResponseKey] = true;
        }

        if (processedResponseKey in fieldNameMap) {
          fieldNameMap[processedResponseKey].push(valueToAdd);
        } else {
          fieldNameMap[processedResponseKey] = [valueToAdd];
        }
      }

      for (let i = customFieldDataValues.length; i < maxCustomFieldsInData; i++) {
        const processedResponseKey = `${CUSTOM_FIELD_KEY_PREFIX}${i + 1}`;
        if (processedResponseKey in fieldNameMap) {
          fieldNameMap[processedResponseKey].push(null);
        } else {
          fieldNameMap[processedResponseKey] = [null];
        }
      }
    });
  });

  Object.keys(fieldNameMap).forEach((fieldName) => {
    if (!shouldFieldBeKept[fieldName]) {
      delete fieldNameMap[fieldName];
    }
  });
  return fieldNameMap;
};
